/** @format */

import ColiverIcon from '@/assets/icons/dovevivo_colivers_icon.svg';
import KitchenIcon from '@/assets/icons/dovevivo_flatroom_kitchen.svg';
import KitchenItems from '@/assets/icons/dovevivo_flatroom_kitchenItems.svg';
import PlatesIcon from '@/assets/icons/dovevivo_flatroom_platesAndGlasses.svg';
import AccessibleIcon from '@/assets/icons/dovevivo_pdp_accessible.svg';
import AirConditioningIcon from '@/assets/icons/dovevivo_pdp_air_conditioning.svg';
import BalconyIcon from '@/assets/icons/dovevivo_pdp_balcony.svg';
import BasementIcon from '@/assets/icons/dovevivo_pdp_basement.svg';
import BathroomIcon from '@/assets/icons/dovevivo_pdp_bathroom.svg';
import BedIcon from '@/assets/icons/dovevivo_pdp_bed.svg';
import BikeIcon from '@/assets/icons/dovevivo_pdp_bike.svg';
import CarIcon from '@/assets/icons/dovevivo_pdp_car.svg';
import DishwasherIcon from '@/assets/icons/dovevivo_pdp_dishwasher.svg';
import ElevatorIcon from '@/assets/icons/dovevivo_pdp_elevator.svg';
import EnergyRatingIcon from '@/assets/icons/dovevivo_pdp_energy_raiting.svg';
import FloorIcon from '@/assets/icons/dovevivo_pdp_floor.svg';
import GardenIcon from '@/assets/icons/dovevivo_pdp_garden.svg';
import HeatingIcon from '@/assets/icons/dovevivo_pdp_heating.svg';
import MicrowaveIcon from '@/assets/icons/dovevivo_pdp_microwave.svg';
import MotorbikeParkingIcon from '@/assets/icons/dovevivo_pdp_motorbikeparking.svg';
import OvenIcon from '@/assets/icons/dovevivo_pdp_oven.svg';
import PoolIcon from '@/assets/icons/dovevivo_pdp_pool.svg';
import SizeIcon from '@/assets/icons/dovevivo_pdp_size.svg';
import Terrace from '@/assets/icons/dovevivo_pdp_terrace.svg';
import WardrobeIcon from '@/assets/icons/dovevivo_pdp_wardrobe.svg';
import WashingMachineIcon from '@/assets/icons/dovevivo_pdp_washing_machine.svg';
import WifiIcon from '@/assets/icons/dovevivo_pdp_wifi.svg';
import WorkstationIcon from '@/assets/icons/dovevivo_pdp_work_station.svg';
import { TTranslation } from '@/i18n/createProvider';
import { useAppTranslation } from '@/i18n/declarations';
import { nth } from '@/i18n/utils/nth';
import { TLocale } from '@/utils/gatsby/types';
import { FormattedMessage } from 'gatsby-plugin-intl';
import React from 'react';
import { TRoom } from '../declarations';

export const useRoomServices = (room: TRoom, coliversCount: number) => {
  const { roomFeatures, flatFeatures } = room;
  const { t, intl } = useAppTranslation();

  const RoomFeatures = [
    {
      name: 'wiFi',
      image: WifiIcon,
      valid: flatFeatures.wiFi,
      text: <FormattedMessage id="pdp.room_features.wifi" />,
      priority: true,
    },
    {
      name: 'size',
      image: SizeIcon,
      valid: roomFeatures.size,
      text: <p>{roomFeatures.size} m²</p>,
      priority: true,
    },
    {
      name: 'wardrobe',
      image: WardrobeIcon,
      valid: true,
      text: <FormattedMessage id="pdp.room_features.wardrobe" />,
      priority: true,
    },
    {
      name: 'workstation',
      image: WorkstationIcon,
      valid: true,
      text: <FormattedMessage id="pdp.room_features.workstation" />,
      priority: true,
    },
    {
      name: 'bathroom',
      image: BathroomIcon,
      valid: roomFeatures.bathroom,
      text: <FormattedMessage id="pdp.room_features.private_bathroom" />,
      priority: false,
    },
    {
      name: 'balcony',
      image: BalconyIcon,
      valid: roomFeatures.balcony,
      text: <FormattedMessage id="pdp.room_features.private_balcony" />,
      priority: false,
    },
  ];

  const FlatFeatures = [
    {
      name: 'floor',
      image: FloorIcon,
      text: `${nth(flatFeatures.floor, intl.locale as TLocale)} ${t('pdp.room_features.floor' as TTranslation)}`,
      valid: flatFeatures.floor,
      priority: true,
    },
    {
      name: 'airConditioning',
      image: AirConditioningIcon,
      text: <FormattedMessage id="pdp.room_features.air_conditioning" />,
      valid: flatFeatures.airConditioning,
      priority: false,
    },
    {
      name: 'balcony',
      image: BalconyIcon,
      text: <FormattedMessage id="pdp.room_features.private_balcony" />,
      valid: flatFeatures.balcony,
      priority: false,
    },
    {
      name: 'elevator',
      image: ElevatorIcon,
      text: <FormattedMessage id="pdp.room_features.elevator" />,
      valid: flatFeatures.elevator,
      priority: true,
    },
    {
      name: 'tenants',
      image: ColiverIcon,
      text: <FormattedMessage id="pdp.room_features.colivers_count" values={{ colivers: coliversCount }} />,
      valid: coliversCount,
      priority: true,
    },
    {
      name: 'garden',
      image: GardenIcon,
      text: <FormattedMessage id="pdp.room_features.garden" />,
      valid: flatFeatures.garden,
      priority: false,
    },
    {
      name: 'washingMachine',
      image: WashingMachineIcon,
      text: <FormattedMessage id="pdp.room_features.washing_machine" />,
      valid: flatFeatures.washingMachine,
      priority: false,
    },
    {
      name: 'dishWasher',
      image: DishwasherIcon,
      text: <FormattedMessage id="pdp.room_features.dishwasher" />,
      valid: flatFeatures.dishWasher,
      priority: false,
    },
    {
      name: 'wiFi',
      image: WifiIcon,
      text: <FormattedMessage id="pdp.room_features.wifi" />,
      valid: flatFeatures.wiFi,
      priority: false,
    },
    {
      name: 'bedsCount',
      image: BedIcon,
      text: (
        <FormattedMessage
          id={flatFeatures.bedsCount > 1 ? 'pdp.room_features.beds_count' : 'pdp.room_features.bed_count'}
          values={{
            count: flatFeatures.bedsCount,
          }}
        />
      ),
      valid: flatFeatures.bedsCount,
      priority: false,
    },
    {
      name: 'roomsCount',
      image: BedIcon,
      text: (
        <FormattedMessage
          id={flatFeatures.roomsCount > 1 ? 'pdp.room_features.rooms_count' : 'pdp.room_features.room_count'}
          values={{
            count: flatFeatures.roomsCount,
          }}
        />
      ),
      valid: flatFeatures.roomsCount,
      priority: false,
    },
    {
      name: 'bathroomCount',
      image: BathroomIcon,
      text: (
        <FormattedMessage
          id={flatFeatures.bathroomCount > 1 ? 'pdp.room_features.bathrooms_count' : 'pdp.room_features.bathroom_count'}
          values={{
            count: flatFeatures.bathroomCount,
          }}
        />
      ),
      valid: flatFeatures.bathroomCount,
      priority: true,
    },
    {
      name: 'heating',
      image: HeatingIcon,
      text: <FormattedMessage id="pdp.room_features.heating" />,
      valid: flatFeatures.heating === 'NotPresent' ? false : true,
      priority: false,
    },
    {
      name: 'kitchenItems',
      image: KitchenItems,
      valid: true,
      text: <FormattedMessage id="pdp.room_features.kitchen_items" />,
    },
    {
      name: 'motorBikeParking',
      image: MotorbikeParkingIcon,
      text: <FormattedMessage id="pdp.room_features.motorbike_parking" />,
      valid: flatFeatures.motorBikeParking,
      priority: false,
    },
    {
      name: 'bikeParking',
      image: BikeIcon,
      text: <FormattedMessage id="pdp.room_features.bike_parking" />,
      valid: flatFeatures.bikeParking,
      priority: false,
    },
    {
      name: 'carParking',
      image: CarIcon,
      text: <FormattedMessage id="pdp.room_features.car_parking" />,
      valid: flatFeatures.carParking,
      priority: false,
    },
    {
      name: 'energyRating',
      image: EnergyRatingIcon,
      text: (
        <FormattedMessage
          id="pdp.room_features.energy_class"
          values={{ class: flatFeatures.energyRating, ipe: flatFeatures.ipe }}
        />
      ),
      valid: flatFeatures.energyRating,
      priority: false,
    },
    {
      name: 'oven',
      image: OvenIcon,
      text: <FormattedMessage id="pdp.room_features.oven" />,
      valid: flatFeatures.oven,
      priority: false,
    },
    {
      name: 'microwave',
      image: MicrowaveIcon,
      text: <FormattedMessage id="pdp.room_features.microwave" />,
      valid: flatFeatures.microwave,
      priority: false,
    },
    {
      name: 'platesAndGlasses',
      image: PlatesIcon,
      text: <FormattedMessage id="pdp.room_features.plates_and_glasses" />,
      valid: true,
      priority: false,
    },
    {
      name: 'pool',
      image: PoolIcon,
      text: <FormattedMessage id="pdp.room_features.pool" />,
      valid: flatFeatures.pool,
      priority: false,
    },
    {
      name: 'sharedKitchen',
      image: KitchenIcon,
      text: <FormattedMessage id="pdp.room_features.shared_kitchen" />,
      valid: true,
      priority: false,
    },
    {
      name: 'terrace',
      image: Terrace,
      text: <FormattedMessage id="pdp.room_features.terrace" />,
      valid: flatFeatures.terrace,
      priority: false,
    },
    {
      name: 'accessible',
      image: AccessibleIcon,
      text: <FormattedMessage id="pdp.room_features.accessible" />,
      valid: flatFeatures.accessible,
      priority: false,
    },
    {
      name: 'basement',
      image: BasementIcon,
      text: <FormattedMessage id="pdp.room_features.basement" />,
      valid: flatFeatures.basement,
      priority: false,
    },
  ];

  return { RoomFeatures, FlatFeatures };
};
